import { Button } from '@hologram-dimension/button';
import { Link } from '@hologram-dimension/link';
import { Modal } from '@hologram-dimension/modal';
import clsx from 'clsx/lite';
import ManageTunnelingForm from 'components/ManageTunneling/ManageTunneling';
import type { CommonModalProps } from 'modals/shared';
import { useCallback, useRef, useState, type FC } from 'react';
import type { HologramSimIdsMap, OnSubmit } from 'types/common';
import { ManageDeviceTunnelingArticle } from 'utils/constants/links';
import formatNumber from 'utils/numbers/formatNumber';
import useSimDetails from 'api/sims/useSimDetails';
import styles from './ManageTunneling.module.css';

interface ManageTunnelingProps extends CommonModalProps {
  sims: HologramSimIdsMap;
  onSuccess: (sims: Array<HologramSimId>) => void;
}

const ManageTunneling: FC<ManageTunnelingProps> = ({
  onClose,
  defaultOpen = false,
  className,
  sims,
  onSuccess,
}) => {
  const simId = sims?.size === 1 ? sims?.keys().next().value : undefined;
  const [open, setOpen] = useState(defaultOpen);
  const [processing, setIsProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const submitRef = useRef<OnSubmit>();

  const onProcessing = useCallback((value: boolean) => {
    setIsProcessing(value);
  }, []);

  const onDone = useCallback(
    (modifiedSims: Array<HologramSimId>) => {
      setDone(true);
      if (modifiedSims) {
        onSuccess(modifiedSims);
      }
    },
    [onSuccess]
  );

  const handleSubmit: React.MouseEventHandler &
    React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    if (submitRef.current) {
      await submitRef.current.onSubmit();
    }
  };
  const { simDetails, isLoading } = useSimDetails(simId);

  let modalHeader = 'Manage secure tunneling';
  if (simId) {
    if (isLoading) {
      modalHeader = 'Manage secure tunneling';
    } else {
      modalHeader =
        simDetails.tunnelable === 1 ? 'Disable secure tunneling' : 'Enable secure tunneling';
    }
  } else {
    modalHeader = `Manage secure tunneling for ${formatNumber(sims.size)} SIMs`;
  }

  return (
    <Modal
      className={clsx(className)}
      header={modalHeader}
      onClosed={onClose}
      open={open}
      setOpen={setOpen}
      noContentPadding
      contentWell={
        <>
          Communicate with your devices using secure, authenticated tunnels via SSH using
          Spacebridge, without relying on a static IP address.{' '}
          <Link href={ManageDeviceTunnelingArticle} target="_blank" rel="noreferrer" type="inline">
            Read the secure tunneling guide
          </Link>
        </>
      }
      footerActions={
        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {done ? 'Close' : 'Cancel'}
          </Button>
          {!done && (
            <Button loading={processing} onClick={handleSubmit} variant="primary">
              {sims.size === 1 ? 'Update SIM' : `Update ${formatNumber(sims.size)} SIMs`}
            </Button>
          )}
        </div>
      }
    >
      <ManageTunnelingForm
        sims={sims}
        onProcessing={onProcessing}
        ref={submitRef}
        onDone={onDone}
      />
    </Modal>
  );
};

export default ManageTunneling;
